import React, { useMemo } from 'react';
import MatchFormationPitch from '@/components/wyscout/MatchFormationPitch';
import type {
  WyscoutLineupPlayer,
  WyscoutLineup,
  WyscoutProviderNumber,
  WyscoutSubstitution,
} from '@/services/api/matchesV2';

interface Props {
  lineup: WyscoutLineup;
  homeTeamName: string;
  awayTeamName: string;
  kickoffAt: string;
  playerNames?: ReadonlyMap<number, string>;
}

interface NumericPlayer {
  playerId: number;
  shirtNumber?: number;
  position: string;
}

interface NumericSubstitution {
  playerIn: number;
  playerOut: number;
  minute: number;
}

const positiveInteger = (value: WyscoutProviderNumber): number | null => {
  if (typeof value === 'string' && !/^\d+$/.test(value.trim())) return null;
  const number = Number(value);
  return Number.isSafeInteger(number) && number > 0 ? number : null;
};

const nonNegativeInteger = (value: WyscoutProviderNumber): number | null => {
  if (typeof value === 'string' && !/^\d+$/.test(value.trim())) return null;
  const number = Number(value);
  return Number.isSafeInteger(number) && number >= 0 ? number : null;
};

const numericPlayer = (player: WyscoutLineupPlayer): NumericPlayer | null => {
  const playerId = positiveInteger(player.playerId);
  if (playerId === null) return null;
  const shirtNumber = player.shirtNumber === undefined ? undefined : nonNegativeInteger(player.shirtNumber) ?? undefined;
  return { playerId, shirtNumber, position: player.position?.trim() || '' };
};

const numericSubstitution = (substitution: WyscoutSubstitution): NumericSubstitution | null => {
  const playerIn = positiveInteger(substitution.playerIn);
  const playerOut = positiveInteger(substitution.playerOut);
  const minute = nonNegativeInteger(substitution.minute);
  return playerIn !== null && playerOut !== null && minute !== null
    ? { playerIn, playerOut, minute }
    : null;
};

const normalizeSide = (
  lineup: WyscoutLineupPlayer[],
  bench: WyscoutLineupPlayer[],
  substitutions: WyscoutSubstitution[],
) => ({
  lineup: lineup.map(numericPlayer).filter((player): player is NumericPlayer => player !== null),
  bench: bench.map(numericPlayer).filter((player): player is NumericPlayer => player !== null),
  substitutions: substitutions.map(numericSubstitution).filter((substitution): substitution is NumericSubstitution => substitution !== null),
});

const validFormation = (schema: string, players: NumericPlayer[], rawCount: number): boolean => {
  const parts = schema.trim().split('-').map(part => Number(part));
  return parts.length >= 2
    && parts.length <= 4
    && parts.every(part => Number.isSafeInteger(part) && part > 0 && part <= 5)
    && parts.reduce((total, part) => total + part, 0) === 10
    && players.length === rawCount
    && players.length === 11
    && players.every(player => player.position.length > 0)
    && new Set(players.map(player => player.playerId)).size === players.length;
};

const shirtLabel = (player: WyscoutLineupPlayer, playerNames?: ReadonlyMap<number, string>): string => {
  const id = positiveInteger(player.playerId);
  const name = id === null ? undefined : playerNames?.get(id)?.trim();
  if (name) return name;
  const shirt = player.shirtNumber === undefined ? null : nonNegativeInteger(player.shirtNumber);
  return shirt === null ? 'Unknown player' : `#${shirt}`;
};

const TeamFallback: React.FC<{
  lineup: WyscoutLineupPlayer[];
  bench: WyscoutLineupPlayer[];
  substitutions: WyscoutSubstitution[];
  playerNames?: ReadonlyMap<number, string>;
}> = ({ lineup, bench, substitutions, playerNames }) => {
  const shirtById = new Map<number, string>();
  for (const player of [...lineup, ...bench]) {
    const id = positiveInteger(player.playerId);
    if (id !== null) shirtById.set(id, shirtLabel(player, playerNames));
  }
  const substitutionLabel = (value: WyscoutProviderNumber): string => {
    const id = positiveInteger(value);
    return id === null ? 'Unknown player' : shirtById.get(id) || 'Unknown player';
  };

  const PlayerList: React.FC<{ title: string; players: WyscoutLineupPlayer[] }> = ({ title, players }) => players.length > 0 ? (
    <div>
      <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</h4>
      <ul className="mt-2 grid min-w-0 gap-2 sm:grid-cols-2">
        {players.map((player, index) => (
          <li key={`${String(player.playerId)}-${index}`} className="flex min-w-0 items-center justify-between gap-2 rounded-md bg-gray-50 px-3 py-2 text-sm dark:bg-gray-800">
            <span className="truncate font-semibold text-gray-950 dark:text-white">{shirtLabel(player, playerNames)}</span>
            <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">{player.position?.trim() || 'Position unavailable'}</span>
          </li>
        ))}
      </ul>
    </div>
  ) : null;

  return (
    <div className="space-y-5">
      <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-900 dark:bg-amber-950/30 dark:text-amber-200">A formation pitch is unavailable, so the recorded lineup is shown as a list.</p>
      <PlayerList title="Starting XI" players={lineup} />
      <PlayerList title="Bench" players={bench} />
      {substitutions.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">Substitutions</h4>
          <ul className="mt-2 flex min-w-0 flex-wrap gap-2">
            {substitutions.map((substitution, index) => {
              const minute = nonNegativeInteger(substitution.minute);
              return (
                <li key={`${String(substitution.playerIn)}-${String(substitution.playerOut)}-${index}`} className="max-w-full rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                  <span className="text-green-700 dark:text-green-300">{substitutionLabel(substitution.playerIn)} on</span>
                  <span className="mx-1 text-gray-400">/</span>
                  <span className="text-red-700 dark:text-red-300">{substitutionLabel(substitution.playerOut)} off</span>
                  {minute !== null && <span className="ml-1 text-gray-500 dark:text-gray-400">({minute}')</span>}
                </li>
              );
            })}
          </ul>
        </div>
      )}
      {lineup.length === 0 && bench.length === 0 && substitutions.length === 0 && (
        <p className="text-sm text-gray-600 dark:text-gray-300">No lineup information is available.</p>
      )}
    </div>
  );
};

const WyscoutLineups: React.FC<Props> = ({ lineup, homeTeamName, awayTeamName, kickoffAt, playerNames }) => {
  const date = /^\d{4}-\d{2}-\d{2}/.exec(kickoffAt)?.[0];
  const home = useMemo(
    () => normalizeSide(lineup.home, lineup.homeBench, lineup.homeSubstitutions),
    [lineup.home, lineup.homeBench, lineup.homeSubstitutions],
  );
  const away = useMemo(
    () => normalizeSide(lineup.away, lineup.awayBench, lineup.awaySubstitutions),
    [lineup.away, lineup.awayBench, lineup.awaySubstitutions],
  );
  const sides = [
    {
      key: 'home',
      name: homeTeamName,
      hasFormation: lineup.homeHasFormation,
      schema: lineup.homeFormationSchema,
      lineup: lineup.home,
      bench: lineup.homeBench,
      substitutions: lineup.homeSubstitutions,
      normalized: home,
    },
    {
      key: 'away',
      name: awayTeamName,
      hasFormation: lineup.awayHasFormation,
      schema: lineup.awayFormationSchema,
      lineup: lineup.away,
      bench: lineup.awayBench,
      substitutions: lineup.awaySubstitutions,
      normalized: away,
    },
  ] as const;

  return (
    <div className="grid min-w-0 grid-cols-1 gap-6 xl:grid-cols-2">
      {sides.map(side => {
        const showPitch = side.hasFormation && validFormation(side.schema, side.normalized.lineup, side.lineup.length);

        return (
          <section key={side.key} className="min-w-0 overflow-hidden rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900 sm:p-5" aria-labelledby={`${side.key}-lineup-heading`}>
            <h2 id={`${side.key}-lineup-heading`} className="mb-4 break-words text-center text-lg font-semibold text-gray-950 dark:text-white">{side.name}</h2>
            {showPitch ? (
              <MatchFormationPitch
                formationSchema={side.schema}
                lineup={side.normalized.lineup}
                bench={side.normalized.bench}
                substitutions={side.normalized.substitutions}
                teamName={side.name}
                matchDate={date}
                disablePerformanceReportRatings
                resolvePlayers={false}
                playerNames={playerNames}
              />
            ) : (
              <TeamFallback lineup={side.lineup} bench={side.bench} substitutions={side.substitutions} playerNames={playerNames} />
            )}
          </section>
        );
      })}
    </div>
  );
};

export default WyscoutLineups;
